import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type Me } from './api';
import { applyTheme, storedThemeId } from './theme';
import { installSpatialNavigation } from './spatial';
import { Login } from './screens/Login';
import { Profiles } from './screens/Profiles';
import { Main } from './screens/Main';
import { InviteScreen } from './screens/Invite';
import { Onboarding } from './screens/Onboarding';
import { ChangePassword } from './screens/ChangePassword';
import { goBack } from './router';

type Screen = { name: 'password' } | { name: 'loading' } | { name: 'login'; welcome?: string } | { name: 'invite'; code: string } | { name: 'onboarding'; me: Me } | { name: 'profiles'; me: Me } | { name: 'home'; me: Me };

export function App() {
  const [screen, setScreen] = useState<Screen>({ name: 'loading' });
  const backHandler = useRef<() => void>(() => {});

  useEffect(() => {
    api.theme(storedThemeId())
      .catch(() => api.theme('tumedia'))
      .then(applyTheme)
      .catch(() => {
        // zonder thema werken de standaardwaarden uit styles.css
      });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      const welkom = location.pathname === '/welkom';
      if (me.account.mustChangePassword) return setScreen({ name: 'password' });
      setScreen(!me.profile ? { name: 'profiles', me } : me.onboarding || welkom ? { name: 'onboarding', me } : { name: 'home', me });
    } catch (err) {
      // Uitnodigingslink: eerst aanmelden, dan inloggen
      const inv = /^\/invite\/([\w-]{0,40})/.exec(location.pathname);
      if (inv) setScreen({ name: 'invite', code: decodeURIComponent(inv[1]) });
      else if (err instanceof ApiError && err.status === 401) setScreen({ name: 'login' });
      else setScreen({ name: 'login' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => installSpatialNavigation(() => backHandler.current()), []);

  // Back-gedrag per scherm (Escape / Back op de afstandsbediening)
  backHandler.current = () => {
    if (screen.name === 'home') goBack();
  };

  switch (screen.name) {
    case 'password':
      return <ChangePassword forced onDone={() => void refresh()} />;
    case 'loading':
      return <div className="center-screen" aria-busy="true"><div className="logo">tu<span>media</span></div></div>;
    case 'login':
      return <Login onLoggedIn={() => { if (location.pathname.startsWith('/invite/')) history.replaceState(null, '', '/'); void refresh(); }} welcome={screen.welcome} />;
    case 'invite':
      return <InviteScreen code={screen.code} onRegistered={(name) => setScreen({ name: 'login', welcome: name })} onLoggedIn={() => { history.replaceState(null, '', '/welkom'); void refresh(); }} />;
    case 'onboarding':
      return (
        <Onboarding
          me={screen.me}
          onDone={() => {
            if (location.pathname === '/welkom' || location.pathname.startsWith('/invite/')) history.replaceState(null, '', '/');
            void refresh();
          }}
        />
      );
    case 'profiles':
      return (
        <Profiles
          me={screen.me}
          onSelected={refresh}
          onLogout={async () => {
            await api.logout().catch(() => undefined);
            setScreen({ name: 'login' });
          }}
        />
      );
    case 'home':
      return <Main me={screen.me} onSwitchProfile={() => setScreen({ name: 'profiles', me: screen.me })} />;
  }
}
